import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Globe, Building2, User, MapPin } from "lucide-react";
import api from "@/lib/api";

import { useSeo } from "@/lib/seo";

// Bio + photo arrive later: leave null and the UI keeps a tidy placeholder hidden.
const TEAM = [
  { name: "Aziz Damani", title: "Founder", bio: null, photo: null },
];

function useCompany() {
  const [co, setCo] = useState(null);
  useEffect(() => { api.get("/company").then(({ data }) => setCo(data)).catch(() => setCo({})); }, []);
  return co;
}

function TeamCard({ m }) {
  const initials = m.name.split(" ").map((w) => w[0]).join("").slice(0, 2);
  return (
    <div data-testid={`team-${m.name.toLowerCase().replace(/\s+/g, "-")}`} className="glass rounded-2xl p-6 border border-white/10 flex items-start gap-5">
      {m.photo ? (
        <img src={m.photo} alt={m.name} className="w-20 h-20 rounded-2xl object-cover border border-amber-500/30" />
      ) : (
        <div data-testid="team-photo-placeholder" aria-hidden="true"
          className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500/30 to-purple-600/30 border border-amber-500/30 flex items-center justify-center font-display font-black text-2xl text-amber-200">
          {initials}
        </div>
      )}
      <div className="min-w-0">
        <p className="font-display text-xl font-bold">{m.name}</p>
        <p className="text-sm text-amber-300 font-semibold">{m.title}</p>
        {m.bio && <p className="text-sm text-gray-400 mt-3 leading-relaxed">{m.bio}</p>}
      </div>
    </div>
  );
}

function ContactLines({ co }) {
  if (!co) return <p className="text-gray-500 text-sm">Loading…</p>;
  return (
    <ul className="space-y-3 text-sm">
      {co.phone && (
        <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-emerald-400" />
          <a data-testid="company-phone" href={`tel:${co.phone_e164}`} className="text-gray-200 hover:text-white font-mono">{co.phone}</a>
        </li>
      )}
      {co.email && (
        <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-cyan-400" />
          <a data-testid="company-email" href={`mailto:${co.email}`} className="text-gray-200 hover:text-white">{co.email}</a>
        </li>
      )}
      {co.website && (
        <li className="flex items-center gap-3"><Globe className="w-4 h-4 text-amber-400" />
          <a data-testid="company-website" href={co.website} className="text-gray-200 hover:text-white">{co.website.replace(/^https?:\/\//, "")}</a>
        </li>
      )}
      {co.address && (
        <li className="flex items-start gap-3"><MapPin className="w-4 h-4 text-rose-400 mt-0.5" />
          <a data-testid="company-address" href={`https://maps.google.com/?q=${encodeURIComponent(co.address)}`} target="_blank" rel="noreferrer" className="text-gray-200 hover:text-white">{co.address}</a>
        </li>
      )}
      {co.company && (
        <li className="flex items-center gap-3"><Building2 className="w-4 h-4 text-fuchsia-400" />
          <span data-testid="company-name" className="text-gray-300">{co.company}{co.parent ? ` · A ${co.parent} company` : ""}</span>
        </li>
      )}
    </ul>
  );
}

export function About() {
  useSeo({ title: "About — Phone Play LLC, a Damani Holdings company", path: "/about", description: "PhonePlay is a free-to-play social gaming platform by Phone Play LLC, a Damani Holdings company based in Norcross, GA. Skill games, daily challenges and a Fun Coin arcade." });
  const co = useCompany();
  return (
    <article data-testid="about-page">
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-4">About PhonePlay</h1>
      <p className="text-gray-400 leading-relaxed mb-3">
        PhonePlay is a free, mobile-first social gaming platform: daily WordVolt and Solitaire challenges, quick skill
        games, 1v1 duels and tournaments against real opponents, and a Fun Coin arcade. Everything is played with Fun
        Coins — 18+, coins have no monetary value, no real-money gambling.
      </p>
      <p className="text-gray-400 leading-relaxed mb-10">
        PhonePlay is operated by <span className="text-gray-200">Phone Play LLC</span>, a{" "}
        <span className="text-gray-200">Damani Holdings</span> company, headquartered at{" "}
        <span data-testid="about-address" className="text-gray-200">5675 Jimmy Carter Blvd, Suite 725, Norcross, GA 30071</span>.
      </p>

      <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><User className="w-5 h-5 text-amber-400" /> Team</h2>
      <div className="space-y-4 mb-10" data-testid="team-section">
        {TEAM.map((m) => <TeamCard key={m.name} m={m} />)}
      </div>

      <h2 className="font-display text-xl font-bold mb-4">Get in touch</h2>
      <div className="glass rounded-2xl p-6 border border-white/10 mb-8"><ContactLines co={co} /></div>
      <Link to="/contact" data-testid="about-contact-link" className="text-cyan-400 hover:underline text-sm">Contact page →</Link>
    </article>
  );
}

export function Contact() {
  useSeo({ title: "Contact Phone Play LLC", path: "/contact", description: "Contact the PhonePlay team: phone, email and office address in Norcross, GA. Help with your account, Fun Coins, skill games or our SMS program." });
  const co = useCompany();
  return (
    <article data-testid="contact-page">
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-4">Contact Us</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Questions about your account, Fun Coins, skill games or our SMS program? Reach the Phone Play LLC team below.
        For help with text messages, reply <span className="font-mono">HELP</span> to any message or <span className="font-mono">STOP</span> to opt out.
      </p>
      <div className="glass rounded-2xl p-6 border border-white/10 mb-6"><ContactLines co={co} /></div>
      <p className="text-xs text-gray-600">
        Phone Play LLC · A Damani Holdings company · 5675 Jimmy Carter Blvd, Suite 725, Norcross, GA 30071 · <Link to="/privacy" className="hover:text-white">Privacy Policy</Link> · <Link to="/terms" className="hover:text-white">Terms &amp; Rules</Link>
      </p>
    </article>
  );
}

export function NotFound() {
  useSeo({ title: "Page not found", path: "/404", description: "That page doesn't exist on PhonePlay.", noindex: true });
  return (
    <article data-testid="not-found-page" className="text-center py-10">
      <p className="font-mono text-7xl font-black text-gradient-coins mb-4">404</p>
      <h1 className="font-display text-2xl sm:text-3xl font-black mb-3">Page not found</h1>
      <p className="text-gray-400 mb-8">That link doesn't exist or has moved. Try one of these instead.</p>
      <div className="flex flex-wrap justify-center gap-3">
        <Link to="/" data-testid="notfound-home-link" className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold">Home</Link>
        <Link to="/how-to-play" className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10">How to Play</Link>
        <Link to="/contact" className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10">Contact</Link>
      </div>
    </article>
  );
}
